import _ from 'underscore';
import PropTypes from 'prop-types';
import React, { useContext, useState, useMemo, useCallback } from 'react';
import classNames from 'classnames';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { conversionTagProp, withConversionTagData, useInitConversionTagData, isDataReady } from '@bingads-webui-campaign-react/conversion-data';
import { IS_EMAIL_SUPPORTED_ENABLED } from '@bingads-webui-campaign-react/conversion-utils';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { SidePanel as Panel } from '@bingads-webui-campaign-react/side-panel';
import { PanelType, DefaultButton, Link } from '@fluentui/react';

import { EmailTagDialog } from './email-tag-dialog';
import { platforms, useHelpIds } from '../utils/platforms';
import { InsertInstruction } from '../components/insert-instruction';
import { TagSVG } from '../components/tag-SVG';
import { CopyTag } from '../components/copy-tag';
import { getEditWebsitePanelStyle, editWebsitePanelStyles } from '../styles/edit-website-panel-styles';

const EditWebsitePanelUnstyled = (props) => {
  const {
    isPanelOpen,
    onClosePanel,
    tagContext,
    onCopyCallback,
    isEmailSupported,
    classes,
  } = props;
  const {
    data,
  } = tagContext;
  const i18n = useContext(I18nContext);
  const helpIds = useHelpIds();
  const allPlatformsLearnMoreLink = helpIds.allPlatformsLearnMore;

  const onRenderPanelFooter = useCallback(() => (
    <DefaultButton onClick={onClosePanel} className={classNames(classes.close, 'close-panel')}>
      {i18n.getString(_TL_('Close'))}
    </DefaultButton>
  ), [i18n, onClosePanel, classes.close]);

  const panelWidth = useMemo(() => {
    if (isDataReady(data)) {
      return editWebsitePanelStyles.panel.width[data.type];
    }
    return '920px';
  }, [data]);

  useInitConversionTagData(tagContext);

  const [showEmailTagDialog, setShowEmailTagDialog] = useState(false);

  const onOpenEmailTagDialog = () => {
    setShowEmailTagDialog(true);
  };

  const onCloseEmailTagDialog = () => {
    setShowEmailTagDialog(false);
  };

  return (
    <Panel
      isOpen={isPanelOpen}
      onOuterClick={_.noop}
      onDismiss={onClosePanel}
      type={PanelType.custom}
      customWidth={panelWidth}
      headerText={i18n.getString(_TL_('Insert the tracking tag in your website\'s code'))}
      onRenderFooterContent={onRenderPanelFooter}
      isFooterAtBottom
      className={classNames(classes.container, 'edit-website-panel')}
      closeButtonAriaLabel={i18n.getString(_TL_('Close panel'))}
      styles={editWebsitePanelStyles.panel}
      isLightDismiss
      focusTrapZoneProps={{
        forceFocusInsideTrap: false,
      }}
    >
      <InsertInstruction />
      {isDataReady(data) && (
        <React.Fragment>
          {_.isString(allPlatformsLearnMoreLink) ? (
            <p><Link href={allPlatformsLearnMoreLink} target="_blank">{i18n.getString(_TL_('Learn more'))}</Link></p>
          ) : (
            <div className={classNames(classes.platforms, 'platforms')}>
              <div className="title">
                {i18n.getString(_TL_('If you use a website platform, follow the instructions for that platform:'))}
              </div>
              <ul>
                {_.map(platforms, (platformText, platformKey) => (
                  <li key={platformKey} className={classNames(classes.platformOption, 'platform-option', `${platformKey}-platform-option`)}>
                    <LearnMoreLink
                      topic={`${helpIds[data.type][platformKey]}`}
                      linkText={`${platformText}`}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}

          <TagSVG tag={data} />
          <CopyTag tag={data} onCopyCallback={onCopyCallback} />
        </React.Fragment>
      )}

      {isEmailSupported &&
        <DefaultButton onClick={onOpenEmailTagDialog} className={classNames(classes.email, 'email-button')}>
          {i18n.getString(_TL_('Send tag to someone else'))}
        </DefaultButton>
      }
      {isEmailSupported &&
        <EmailTagDialog
          isHidden={!showEmailTagDialog}
          onCloseEmailTagDialog={onCloseEmailTagDialog}
        />
      }
    </Panel>
  );
};

EditWebsitePanelUnstyled.propTypes = {
  isPanelOpen: PropTypes.bool.isRequired,
  onClosePanel: PropTypes.func.isRequired,
  tagContext: conversionTagProp.isRequired,
  onCopyCallback: PropTypes.func.isRequired,
  isEmailSupported: PropTypes.bool,
  classes: PropTypes.shape(_.mapObject(
    getEditWebsitePanelStyle(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
};

EditWebsitePanelUnstyled.defaultProps = {
  isEmailSupported: IS_EMAIL_SUPPORTED_ENABLED,
};

export const EditWebsitePanel = withDefaultStyles(
  withConversionTagData(EditWebsitePanelUnstyled),
  getEditWebsitePanelStyle
);
